import React from 'react';
import type { LstPeriodView } from '../PeriodSwitcher';
import { MeuPadraoHeaderFilters } from '../MeuPadraoHeaderFilters';
import type { LeaderSelectOption } from '../MeuPadraoSubHeader';
import type { KamishibaiMatrixStats, KamishibaiMatrixViewMode } from '../../hooks/useKamishibaiMatrixModel';

export interface KamishibaiMatrixSubHeaderProps {
  periodView: LstPeriodView;
  onPeriodChange: (view: LstPeriodView) => void;
  title: string;
  contextLine: string;
  viewMode: KamishibaiMatrixViewMode;
  onViewModeChange: (mode: KamishibaiMatrixViewMode) => void;
  stats: KamishibaiMatrixStats;
  statsMode: KamishibaiMatrixViewMode;
  leaderOptions?: LeaderSelectOption[];
  selectedLeaderId?: string;
  onLeaderChange?: (userId: string) => void;
  viewingSubordinate?: boolean;
}

/** Sub-header Kamishibai — linha única: contexto+métricas | visão | filtros. */
export const KamishibaiMatrixSubHeader = React.memo<KamishibaiMatrixSubHeaderProps>(
  function KamishibaiMatrixSubHeader({
    periodView,
    onPeriodChange,
    title,
    contextLine,
    viewMode,
    onViewModeChange,
    stats,
    statsMode,
    leaderOptions,
    selectedLeaderId,
    onLeaderChange,
    viewingSubordinate = false,
  }) {
    return (
      <header className="flex w-full shrink-0 flex-row items-center border-b border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-3">
          {/* Esquerda — título, contexto e métricas compactas */}
          <div className="flex min-w-0 items-center gap-3">
            <div className="min-w-0 shrink">
              <p className="truncate text-[13px] font-bold leading-tight text-slate-800 dark:text-slate-100">
                {title}
                {viewingSubordinate && (
                  <span className="ml-1.5 rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-semibold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
                    Visualização
                  </span>
                )}
              </p>
              <p className="truncate text-[11px] text-slate-500 dark:text-slate-400">{contextLine}</p>
            </div>
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-slate-600 md:hidden dark:bg-slate-800 dark:text-slate-300">
              {stats.pct}%
            </span>
            <div className="hidden shrink-0 items-center gap-2 border-l border-slate-200 pl-3 dark:border-slate-700 md:flex">
              <MetricMini label="Realizadas" value={stats.ok} tone="emerald" />
              <MetricMini label="Desvios" value={stats.desvio} tone="amber" />
              <MetricMini label="Perdas" value={stats.perdido} tone="red" />
              {statsMode === 'hourly' && (stats.alertBlocks ?? 0) > 0 && (
                <MetricMini label="Alertas" value={stats.alertBlocks!} tone="amber" />
              )}
              <MetricMini label="Aderência" value={`${stats.pct}%`} tone="neutral" />
            </div>
          </div>

          {/* Centro — alternância de visão */}
          <div className="justify-self-center">
            <div className="inline-flex overflow-hidden rounded-[5px] border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900">
              <ViewTab
                active={viewMode === 'activities'}
                onClick={() => onViewModeChange('activities')}
                label="Visão por Atividades"
              />
              <ViewTab
                active={viewMode === 'hourly'}
                onClick={() => onViewModeChange('hourly')}
                label="Visão por Horário do Turno"
              />
            </div>
          </div>

          {/* Direita — líder + período (idêntico ao Meu Padrão Dia) */}
          <div className="justify-self-end">
            <MeuPadraoHeaderFilters
              periodView={periodView}
              onPeriodChange={onPeriodChange}
              leaderOptions={leaderOptions}
              selectedLeaderId={selectedLeaderId}
              onLeaderChange={onLeaderChange}
            />
          </div>
        </div>
      </header>
    );
  },
);

const ViewTab: React.FC<{ active: boolean; onClick: () => void; label: string }> = ({
  active,
  onClick,
  label,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`whitespace-nowrap rounded px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide transition-colors duration-100 ${
      active
        ? 'bg-[#2F6FED] text-white'
        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
    }`}
  >
    {label}
  </button>
);

const MetricMini: React.FC<{
  label: string;
  value: number | string;
  tone: 'emerald' | 'amber' | 'red' | 'neutral';
}> = ({ label, value, tone }) => {
  const valueCls = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    neutral: 'text-slate-800 dark:text-slate-100',
  }[tone];

  return (
    <div className="flex flex-col items-center rounded border border-slate-200/80 bg-white px-2 py-0.5 dark:border-slate-700 dark:bg-slate-900/50">
      <span className={`text-[12px] font-bold leading-none tabular-nums ${valueCls}`}>{value}</span>
      <span className="mt-0.5 text-[8px] font-semibold uppercase tracking-wide text-slate-400">{label}</span>
    </div>
  );
};

export default KamishibaiMatrixSubHeader;
